import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Controller } from "react-hook-form";
import { sortCzech } from "@ckgo2/shared";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { useEditPage } from "@/hooks/useEditPage";
import DestinationPicker from "@/components/common/DestinationPicker";
import { enumLabel } from "@/i18n/enumLabels";
import ErrorBanner from "@/components/common/ErrorBanner";
import SaveSuccessIndicator from "@/components/common/SaveSuccessIndicator";
import ValidationErrorBanner from "@/components/common/ValidationErrorBanner";
import { api, type ApiResponse } from "@/api/client";
import { supplierCreateSchema } from "@ckgo2/shared/schemas";
import {
  SUPPLIER_TYPES,
  SUPPLIER_PAYMENT_PROFILES,
  SUPPLIER_TYPE_LABELS,
  SUPPLIER_PAYMENT_PROFILE_LABELS,
  BOOKING_MODES,
  BOOKING_MODE_LABELS,
  type SupplierType,
  type SupplierPaymentProfile,
  type BookingMode,
} from "@ckgo2/shared/types";

interface FormData {
  name: string;
  type: SupplierType;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  currency: string;
  paymentProfile: SupplierPaymentProfile;
  bookingMode: BookingMode;
  paymentTerms: number | null;
  commissionPct: number | null;
  destinationId: string | null;
  notes: string;
  isActive: boolean;
  emailTemplateSlug: string | null;
}

interface EmailTemplate {
  slug: string;
  name: string;
}

interface Supplier extends FormData {
  id: string;
}

export default function SupplierEditPage() {
  const { t } = useTranslation();

  // Active email templates for the per-supplier override picker (Block 4.12 Řez 2).
  const { data: tplData } = useQuery({
    queryKey: ["email-templates"],
    queryFn: () =>
      api.get<ApiResponse<EmailTemplate[]>>("/api/admin/templates?type=email&is_active=true"),
  });

  const { isNew, isLoading, form, saveMutation, onSubmit, itemData } = useEditPage<
    FormData,
    Supplier
  >({
    resourceKey: "supplier",
    listKey: "suppliers",
    apiPath: "/api/admin/suppliers",
    schema: supplierCreateSchema,
    defaultValues: {
      name: "",
      type: "hotel",
      contactName: "",
      contactEmail: "",
      contactPhone: "",
      currency: "CZK",
      paymentProfile: "invoice",
      bookingMode: "confirm_required",
      paymentTerms: null,
      commissionPct: null,
      destinationId: null,
      notes: "",
      isActive: true,
      emailTemplateSlug: null,
    },
    navigateTo: "/suppliers",
    transformOnLoad: (s) => ({
      name: s.name || "",
      type: s.type || "hotel",
      contactName: s.contactName || "",
      contactEmail: s.contactEmail || "",
      contactPhone: s.contactPhone || "",
      currency: s.currency || "CZK",
      paymentProfile: s.paymentProfile || "invoice",
      bookingMode: s.bookingMode || "confirm_required",
      paymentTerms: s.paymentTerms ?? null,
      commissionPct: s.commissionPct ?? null,
      destinationId: s.destinationId ?? null,
      notes: s.notes || "",
      isActive: s.isActive ?? true,
      emailTemplateSlug: s.emailTemplateSlug ?? null,
    }),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { isSubmitting, errors },
  } = form;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  if (!isNew && !isLoading && !itemData) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p className="text-lg text-gray-600 dark:text-gray-400">
          {t("opsAdmin.suppliers.notFound")}
        </p>
        <Link to="/suppliers" className="btn-primary">
          <ArrowLeft className="h-5 w-5" />
          {t("common.actions.back")}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/suppliers" className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700">
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew ? t("opsAdmin.suppliers.new") : t("opsAdmin.suppliers.editTitle")}
            </h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              {isNew
                ? t("opsAdmin.suppliers.newSubtitle")
                : t("opsAdmin.common.editingName", { name: itemData?.name })}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <SaveSuccessIndicator isSuccess={saveMutation.isSuccess} />
          <button
            type="button"
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
            className="btn-primary"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                {t("common.actions.saving")}
              </>
            ) : (
              <>
                <Save className="h-5 w-5" />
                {t("common.actions.save")}
              </>
            )}
          </button>
        </div>
      </div>

      <ErrorBanner error={saveMutation.error} />
      <ValidationErrorBanner errors={errors} />

      <form onSubmit={handleSubmit(onSubmit)} className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="card space-y-4">
            <div>
              <label htmlFor="name" className="label">
                {t("common.fields.name")} *
              </label>
              <input
                {...register("name")}
                type="text"
                id="name"
                className="input"
                placeholder={t("opsAdmin.suppliers.namePlaceholder")}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="type" className="label">
                  {t("common.fields.type")}
                </label>
                <select {...register("type")} id="type" className="input">
                  {SUPPLIER_TYPES.map((st) => (
                    <option key={st} value={st}>
                      {enumLabel(SUPPLIER_TYPE_LABELS, st)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="destinationId" className="label">
                  {t("common.fields.destination")}
                </label>
                <Controller
                  name="destinationId"
                  control={control}
                  render={({ field }) => (
                    <DestinationPicker
                      id="destinationId"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label htmlFor="contactName" className="label">
                  {t("opsAdmin.suppliers.contactName")}
                </label>
                <input
                  {...register("contactName")}
                  type="text"
                  id="contactName"
                  className="input"
                />
              </div>
              <div>
                <label htmlFor="contactEmail" className="label">
                  {t("opsAdmin.suppliers.contactEmail")}
                </label>
                <input
                  {...register("contactEmail")}
                  type="email"
                  id="contactEmail"
                  className="input"
                />
              </div>
              <div>
                <label htmlFor="contactPhone" className="label">
                  {t("opsAdmin.suppliers.contactPhone")}
                </label>
                <input
                  {...register("contactPhone")}
                  type="text"
                  id="contactPhone"
                  className="input"
                />
              </div>
            </div>

            <div>
              <label htmlFor="emailTemplateSlug" className="label">
                {t("opsAdmin.suppliers.emailTemplate")}
              </label>
              <select {...register("emailTemplateSlug")} id="emailTemplateSlug" className="input">
                <option value="">{t("opsAdmin.suppliers.emailTemplateDefault")}</option>
                {sortCzech(tplData?.data ?? [], (tpl) => tpl.name).map((tpl) => (
                  <option key={tpl.slug} value={tpl.slug}>
                    {tpl.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-400">
                {t("opsAdmin.suppliers.emailTemplateHint")}
              </p>
            </div>

            <div>
              <label htmlFor="notes" className="label">
                {t("common.fields.notes")}
              </label>
              <textarea
                {...register("notes")}
                id="notes"
                rows={3}
                className="input"
                placeholder={t("opsAdmin.suppliers.notesPlaceholder")}
              />
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="card space-y-4">
            <div>
              <label htmlFor="currency" className="label">
                {t("common.fields.currency")}
              </label>
              <input
                {...register("currency")}
                type="text"
                id="currency"
                maxLength={3}
                className="input uppercase"
                placeholder="CZK"
              />
            </div>

            <div>
              <label htmlFor="paymentProfile" className="label">
                {t("opsAdmin.suppliers.paymentProfile")}
              </label>
              <select {...register("paymentProfile")} id="paymentProfile" className="input">
                {SUPPLIER_PAYMENT_PROFILES.map((p) => (
                  <option key={p} value={p}>
                    {enumLabel(SUPPLIER_PAYMENT_PROFILE_LABELS, p)}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-400">
                {t("opsAdmin.suppliers.paymentProfileHint")}
              </p>
            </div>

            <div>
              <label htmlFor="bookingMode" className="label">
                {t("opsAdmin.common.bookingMode")}
              </label>
              <select {...register("bookingMode")} id="bookingMode" className="input">
                {BOOKING_MODES.map((m) => (
                  <option key={m} value={m}>
                    {enumLabel(BOOKING_MODE_LABELS, m)}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-400">
                {t("opsAdmin.suppliers.bookingModeHint")}
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="paymentTerms" className="label">
                  {t("opsAdmin.suppliers.paymentTerms")}
                </label>
                <input
                  {...register("paymentTerms", { valueAsNumber: true })}
                  type="number"
                  id="paymentTerms"
                  className="input"
                  placeholder="14"
                />
              </div>
              <div>
                <label htmlFor="commissionPct" className="label">
                  {t("opsAdmin.suppliers.commission")}
                </label>
                <input
                  {...register("commissionPct", { valueAsNumber: true })}
                  type="number"
                  id="commissionPct"
                  className="input"
                  placeholder="10"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <input
                {...register("isActive")}
                type="checkbox"
                id="isActive"
                className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
              />
              <label
                htmlFor="isActive"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t("common.status.active")}
              </label>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
